import { Avatar, Dropdown, Layout, Menu, Typography } from 'antd'
import { Link, useRouter } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuth, useLogout } from '../hooks/useAuth'
import { useIsSuperuser } from '../hooks/useUserRoles'
import type { MenuProps } from 'antd'
import type { ReactNode } from 'react'

const { Header, Sider, Content } = Layout
const { Title } = Typography

interface AdminLayoutProps {
  children: ReactNode
}

const baseItems: MenuProps['items'] = [
  { key: '', label: <Link to="/">首页</Link> },
  { key: 'batch', label: <Link to="/batch">考试项目管理</Link> },
  { key: 'positionCode', label: <Link to="/positionCode">职位代码管理</Link> },
  { key: 'course', label: <Link to="/course">课程管理</Link> },
  { key: 'class', label: <Link to="/class">班级管理</Link> },

  { key: 'order', label: <Link to="/order">订单管理</Link> },
  { key: 'region', label: <Link to="/region">住宿管理</Link> },
  {
    key: 'agreementTemplate',
    label: <Link to="/agreementTemplate">学员协议管理</Link>,
  },
  { key: 'user', label: <Link to="/user">用户管理</Link> },
  { key: 'carousel', label: <Link to="/carousel">轮播图管理</Link> },
  { key: 'blacklist', label: <Link to="/blacklist">黑名单管理</Link> },
  {type: 'divider'},
  { key: 'analytics', label: <Link to="/analytics">行为日志</Link> },
]

const superuserItems: MenuProps['items'] = [
  { type: 'divider' },
  { key: 'adminRegister', label: <Link to="/adminRegister">注册管理员</Link> },
]

const userMenuItems: MenuProps['items'] = [{ key: 'logout', label: '退出登录' }]

export default function AdminLayout({ children }: AdminLayoutProps) {
  const logoutMutation = useLogout()
  const { isAuthenticated: authenticated } = useAuth()
  const { isSuperuser, isLoading: isCheckingRoles } = useIsSuperuser()

  // 此处使用router用于页面刷新时监控路由，确定Menu状态
  const router = useRouter()
  const path = router.latestLocation.pathname

  // 过滤path中的/和参数
  const pathName = path.replace('/', '').split('?')[0]
  const [selectedKey, setSelectedKey] = useState(pathName)

  // 根据用户角色动态生成菜单项
  const menuItems: MenuProps['items'] = isSuperuser
    ? [...baseItems, ...superuserItems]
    : baseItems

  // 如果在登录页面，不显示管理布局
  if (path === '/login') {
    return <>{children}</>
  }

  // 如果未认证，显示空白页面（此时会触发路由守卫跳转到登录页）
  if (!authenticated) {
    return <>{children}</>
  }

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      logoutMutation.mutate()
    }
  }
  useEffect(() => {
    const un = router.subscribe('onBeforeLoad', (ctx) => {
      setSelectedKey(ctx.toLocation.pathname.replace('/', '').split('?')[0])
    })
    return () => {
      un()
    }
  }, [])
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          公考报名项目后台
        </Title>
        <Dropdown
          menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
          placement="bottomRight"
        >
          <Avatar style={{ backgroundColor: '#1890ff', cursor: 'pointer' }}>
            U
          </Avatar>
        </Dropdown>
      </Header>
      <Layout>
        <Sider width={200} style={{ background: '#fff' }}>
          <Menu
            mode="inline"
            // defaultSelectedKeys={['1']}
            selectedKeys={[selectedKey]}
            onClick={({ key }) => setSelectedKey(key)}
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
          />
        </Sider>
        <Content style={{ padding: '24px' }}>{children}</Content>
      </Layout>
    </Layout>
  )
}
